/**
 * applySystemFieldConstraints
 *
 * Enforces app-shipped FHIR/system constraints that facility or admin config
 * cannot override:
 *   - locked fields stay enabled and required
 *   - locked fields cannot be removed from a field set
 *   - locked sections stay enabled and cannot be dropped from the section list
 *
 * Source of truth: systemFieldConstraints.json (never merged from overrides).
 * Canonical field definitions: doctorConsoleFieldSets.json.
 */

import systemConstraints from "../schema/systemFieldConstraints.json";
import canonicalFieldSets from "../schema/doctorConsoleFieldSets.json";

const sortByOrder = (items = []) =>
  [...items].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

const getFieldConstraint = (fieldSetId, fieldId) =>
  systemConstraints.fields?.[fieldSetId]?.[fieldId] ?? null;

const getSectionConstraint = (sectionId) =>
  systemConstraints.sections?.[sectionId] ?? null;

const isSectionLocked = (sectionId) => {
  const constraint = getSectionConstraint(sectionId);
  return constraint?.locked === true || constraint?.disableable === false;
};

const applyLockedField = (field, constraint, canonicalField) => {
  const base = field ?? canonicalField;
  if (!base) return null;

  return {
    ...base,
    enabled: true,
    system: {
      ...(base.system ?? {}),
      ...constraint,
      locked: true,
    },
    validation: {
      ...(base.validation ?? {}),
      required: constraint.fhirRequired !== false,
    },
    visible:
      constraint.allowConditionalVisibility === true
        ? (field?.visible ?? base.visible ?? { when: "always" })
        : { when: "always" },
  };
};

/**
 * Merges facility/admin field overrides with system constraints.
 * Locked fields are always re-injected from canonical definitions when missing.
 */
export const applyFieldConstraints = (fields = [], fieldSetId, tableColumns = []) => {
  if (!fieldSetId) {
    return { fields, tableColumns };
  }

  const fieldConstraints = systemConstraints.fields?.[fieldSetId];
  if (!fieldConstraints) {
    return { fields, tableColumns };
  }

  const canonicalFields = canonicalFieldSets.fieldSets?.[fieldSetId]?.fields ?? [];
  const fieldMap = new Map(fields.map((field) => [field.id, { ...field }]));

  Object.entries(fieldConstraints).forEach(([fieldId, constraint]) => {
    if (!constraint?.locked) return;

    const canonical = canonicalFields.find((field) => field.id === fieldId);
    const merged = applyLockedField(fieldMap.get(fieldId), constraint, canonical);
    if (merged) {
      fieldMap.set(fieldId, merged);
    }
  });

  const lockedColumnKeys = new Set(
    Object.entries(fieldConstraints)
      .filter(([, constraint]) => constraint?.locked && constraint?.fhirRequired)
      .map(([fieldId]) => {
        const canonical = canonicalFields.find((field) => field.id === fieldId);
        return canonical?.key ?? fieldId;
      }),
  );

  const columnMap = new Map(tableColumns.map((column) => [column.key, { ...column }]));
  lockedColumnKeys.forEach((key) => {
    const existing = columnMap.get(key);
    if (existing) {
      columnMap.set(key, { ...existing, enabled: true });
      return;
    }

    const canonicalColumn = canonicalFieldSets.fieldSets?.[fieldSetId]?.tableColumns?.find(
      (column) => column.key === key,
    );
    if (canonicalColumn) {
      columnMap.set(key, { ...canonicalColumn, enabled: true });
    }
  });

  return {
    fields: sortByOrder([...fieldMap.values()]),
    tableColumns: sortByOrder([...columnMap.values()]),
  };
};

/**
 * Merges section overrides while preserving locked sections from the base config.
 */
export const applySectionConstraints = (baseSections = [], overrideSections = null) => {
  if (!overrideSections?.length) {
    return baseSections.map((section) => protectSection(section));
  }

  const baseById = new Map(baseSections.map((section) => [section.id, section]));
  const overrideById = new Map(overrideSections.map((section) => [section.id, section]));

  const merged = baseSections.map((baseSection) => {
    const override = overrideById.get(baseSection.id);
    if (!override) return protectSection(baseSection);
    return protectSection({
      ...baseSection,
      ...override,
      icon: baseSection.icon,
    });
  });

  overrideSections.forEach((overrideSection) => {
    if (!baseById.has(overrideSection.id) && !isSectionLocked(overrideSection.id)) {
      merged.push(overrideSection);
    }
  });

  return sortByOrder(merged);
};

const protectSection = (section) => {
  const constraint = getSectionConstraint(section?.id);
  if (!constraint) return section;

  const protectedSection = {
    ...section,
    system: {
      ...(section.system ?? {}),
      ...constraint,
    },
  };

  if (constraint.locked || constraint.disableable === false) {
    protectedSection.enabled = true;
  }

  if (protectedSection.subSections?.length) {
    protectedSection.subSections = protectedSection.subSections.map((sub) => {
      const subFieldSetId = sub.fieldSet;
      if (!subFieldSetId) return sub;

      const subFields = sub.fields?.length
        ? sub.fields
        : canonicalFieldSets.fieldSets?.[subFieldSetId]?.fields ?? [];

      const { fields } = applyFieldConstraints(subFields, subFieldSetId, []);
      return {
        ...sub,
        fields,
      };
    });
  }

  return protectedSection;
};

export const isSystemLockedField = (fieldSetId, fieldId) =>
  getFieldConstraint(fieldSetId, fieldId)?.locked === true;

export const isSystemLockedSection = (sectionId) => isSectionLocked(sectionId);

export { systemConstraints };
